"use client";

import { useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconSettings from "@/app/(components)/generics/Icons/IconSettings";
import IconSignOut from "@/app/(components)/generics/Icons/IconSignOut";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import Picture from "@/app/(components)/generics/Picture/Picture";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PublicUser } from "@/app/types/auth/PublicUser";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { AUTH_CLEAR_SESSION_PATH } from "@/app/vars/Urls";
import sidebarStyles from "../../Sidebar.module.css";
import styles from "./SidebarAccount.module.css";

type Props = {
  user: PublicUser;
  isCollapsed: boolean;
  lang: string;
};

export default function SidebarAccount(props: Props) {
  const T = useT();
  const { user, isCollapsed, lang } = props;
  const [isOpenUserPopover, setIsOpenUserPopover] = useState(false);

  const handleOpenSettings = () => {
    setIsOpenUserPopover(false);
    ModalStore.getState().openModal(
      Components.Settings,
      T(TKey.SETTINGS),
      { lang, params: { lang } },
      ModalType.MEDIUM,
    );
  };

  const handleLogout = () => {
    window.location.href = AUTH_CLEAR_SESSION_PATH;
  };

  return (
    <Popover
      isVisible={isOpenUserPopover}
      setIsVisible={setIsOpenUserPopover}
      openAfterClick={true}
      closeWhenClickOutside={true}
      isPortaled={!isCollapsed}
      className={isCollapsed ? styles.collapsedAccountWrapper : ""}
    >
      <PopoverTrigger
        className={UStyles.classNames(
          isCollapsed ? styles.collapsedAccountWrapper : styles.accountButton,
          !isCollapsed && isOpenUserPopover ? styles.activeItem : "",
        )}
      >
        {isCollapsed ? (
          <Tooltip
            position={TooltipPosition.RIGHT}
            gap={Limits.SIDEBAR_TOOLTIP_GAP}
            type={TooltipType.GREEN}
            content={user.firstName ? `${user.firstName} ${user.lastName}` : user.email}
            className={sidebarStyles.fullWidth}
            disabled={isOpenUserPopover}
            trigger={
              <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <div
                  className={UStyles.classNames(
                    styles.collapsedAccount,
                    isOpenUserPopover ? styles.activeItem : "",
                  )}
                >
                  <Picture
                    className={styles.avatarImg}
                    picture={user.avatarUrl}
                    displayLetter={(user.firstName || user.email || "?").at(0)?.toUpperCase()}
                  />
                </div>
              </div>
            }
          />
        ) : (
          <div className={styles.userTriggerContent}>
            <div className={styles.userTriggerLeft}>
              <Picture
                className={styles.avatarImg}
                picture={user.avatarUrl}
                displayLetter={(user.firstName || user.email || "?").at(0)?.toUpperCase()}
              />

              <div className={styles.userInfo}>
                <span className={styles.userName}>
                  {user.firstName || ""} {user.lastName || ""}
                </span>
                {/* <span className={styles.userRole}>{user.role}</span> */}
              </div>
            </div>

            <div className={styles.accountChevron}>
              {isOpenUserPopover ? <IconArrowUp size={21} /> : <IconArrowDown size={21} />}
            </div>
          </div>
        )}
      </PopoverTrigger>

      <PopoverContent
        position={
          !isCollapsed
            ? { default: { bottom: "100%", left: "0" } }
            : {
                default: {
                  bottom: "0",
                  left: `calc(100% + ${Limits.SIDEBAR_TOOLTIP_GAP}px)`,
                },
              }
        }
        className={UStyles.classNames(
          styles.accountPopover,
          isCollapsed ? sidebarStyles.collapsedPopover : "",
        )}
      >
        <div className={styles.accountData}>
          <h3>
            {user.firstName} {user.lastName}
          </h3>
          <p>{user.email}</p>
        </div>

        <div className={styles.accountPopoverSeparator} />

        <button className={styles.accountPopoverElement} onClick={handleOpenSettings}>
          <IconSettings />
          {T(TKey.SETTINGS)}
        </button>

        <button className={styles.accountPopoverElement} onClick={handleLogout}>
          <IconSignOut />
          {T(TKey.SIGN_OUT)}
        </button>
      </PopoverContent>
    </Popover>
  );
}
